import React from "react";
import { Box, useColorModeValue } from "@chakra-ui/react";
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  Cell,
  ReferenceLine,
} from "recharts";

export interface CheckinRecord {
  checkInTime: Date;
  checkOutTime?: Date;
  isCheckedOut: boolean;
}

interface BurnoutTrendProps {
  checkInRecords: CheckinRecord[];
  month: number;
  year: number;
}

const BurnoutTrend: React.FC<BurnoutTrendProps> = ({
  checkInRecords,
  month,
  year,
}) => {
  interface DailySession {
    in: Date;
    out: Date;
  }
  interface DailyDataItem {
    day: number;
    hours: number;
    sessions: DailySession[];
  }
  // Number of days in the selected month
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  // Build daily data array: { day, hours }
  const dailyData: DailyDataItem[] = Array.from({ length: daysInMonth }, (_, i) => {
    const day = i + 1;
    const sessions = checkInRecords
      .filter(
        (rec) =>
          rec.isCheckedOut &&
          rec.checkOutTime &&
          rec.checkInTime.getFullYear() === year &&
          rec.checkInTime.getMonth() === month &&
          rec.checkInTime.getDate() === day
      )
      .map((rec) => ({ in: rec.checkInTime, out: rec.checkOutTime! }));
    const hours = sessions.reduce(
      (sum, s) => sum + (s.out.getTime() - s.in.getTime()) / (1000 * 3600),
      0
    );
    return { day, hours: +hours.toFixed(1), sessions };
  });

  // Theme-aware colors
  const gridColor = useColorModeValue("#E2E8F0", "#4A5568");
  const axisLineColor = useColorModeValue("#E2E8F0", "#4A5568");
  const tickColor = useColorModeValue("#2D3748", "#E2E8F0");

  const formatTime = (d: Date) =>
    d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  const CustomTooltip = ({ active, payload, label }: any) => {
    const isDark = useColorModeValue(false, true);
    if (active && payload && payload.length) {
      const data: DailyDataItem = payload[0].payload as DailyDataItem;
      return (
        <div className={`${isDark ? 'bg-[#1A1C1F] border border-[#2E2F33]' : 'bg-white border border-gray-200'} rounded-md shadow-md p-3`}>
          <div className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>Day {label}</div>
          <div className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'} mb-2`}>Total: {data.hours} hrs</div>
          {data.sessions.length > 0 ? (
            <div className="space-y-1">
              {data.sessions.map((s, idx) => (
                <div key={idx} className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  {formatTime(s.in)} – {formatTime(s.out)}
                </div>
              ))}
            </div>
          ) : (
            <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>No check-ins</div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <Box
      p={4}
      width="100%"
      height="100%"
      borderRadius="lg"
      bg={useColorModeValue("white", "#1A1C1F")}
    >

      <div className="flex justify-center items-center mb-2 space-x-6">
      <div className="flex items-center space-x-1">
        <span className="w-3 h-3 square-full bg-yellow-200"></span>
        <span className={`text-xs ${useColorModeValue('text-gray-700', 'text-white')}`}>Underworking (&lt; 8h)</span>
      </div>
      <div className="flex items-center space-x-1">
        <span className="w-3 h-3 square-full bg-blue-200"></span>
        <span className={`text-xs ${useColorModeValue('text-gray-700', 'text-white')}`}>Normal (8–9h)</span>
      </div>
      <div className="flex items-center space-x-1">
        <span className="w-3 h-3 square-full bg-red-200"></span>
        <span className={`text-xs ${useColorModeValue('text-gray-700', 'text-white')}`}>Burnout (&gt; 9h)</span>
      </div>
    </div>

      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={dailyData}
          margin={{ top: 10, right: 20, left: 0, bottom: 20 }}
        >
          <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
          <XAxis
            dataKey="day"
            axisLine={{ stroke: axisLineColor }}
            tickLine={false}
            tick={{ fill: tickColor, fontSize: 12 }}
            tickMargin={8}
            label={{
              value: "Day",
              position: "insideBottom",
              offset: -5,
              fill: tickColor,
              dy: 10,
            }}
          />
          <YAxis
            axisLine={{ stroke: axisLineColor }}
            tickLine={false}
            tick={{ fill: tickColor, fontSize: 12 }}
            unit="h"
            domain={[0, 14]} // cap at 12 so ticks align
            ticks={[0, 2, 4, 6, 8, 10, 12, 14]} // explicit intervals
            label={{
              value: "Hours",
              angle: -90,
              position: "insideLeft",
              fill: tickColor,
              dy: 20, // nudge it left
            }}
          />
          <ReferenceLine
            y={9}
            stroke="#6B7280"
            strokeDasharray="4 4"
            ifOverflow="extendDomain"
            label={{ value: "9h", position: "right", fill: tickColor, fontSize: 12 }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="hours">
            {dailyData.map((entry, index) => {
              // Color logic: <8 = yellow, 8–9 = blue, >9 = red
              const color =
                entry.hours > 9
                  ? "#FCA5A5" // light red
                  : entry.hours >= 8
                  ? "#93C5FD" // light blue
                  : "#FDE68A"; // light yellow
              return <Cell key={`cell-${index}`} fill={color} />;
            })}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default BurnoutTrend;
